import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import {
  ScreenData,
  shouldShowCategory,
  getUpcomingSalahLookaheadDays,
} from "../../types/ScreenTypes";
import { Playlist } from "../../types/index";
import SimpleEmbeddedSlideshowComponent from "./SimpleEmbeddedSlideshowComponent";
import SimpleUpcomingSalahChangesSlideshow from "./SimpleUpcomingSalahChangesSlideshow";
import SimpleNoticeboardDisplay from "./SimpleNoticeboardDisplay";
import SimpleUpcomingEventsWidget from "./SimpleUpcomingEventsWidget";
import {
  fetchEventsICS,
  hasUpcomingEvents,
  hydrateEvents,
} from "../../services/eventsService";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import { fetchNext7DaysPrayerTimes } from "../../services/api";
import { EnhancedPlaylistScheduler } from "../../schedulers/EnhancedPlaylistScheduler";
import { ScheduleType } from "../../types/schedule.types";
import { getRotatedItems } from "../../utils/rotationUtils";
import { useHomePageGapCoordinator } from "../../hooks/useHomePageGapCoordinator";
import {
  getUrgentInjectCadence,
  mergeInjectItems,
  sortByPriorityDesc,
  splitUrgentInjectPlaylists,
} from "../../utils/urgentInjectUtils";
import "./SimplePlaylistDisplayComponent.css";
import useLabbaikItems, { labbaikEvents } from "../../hooks/useLabbaikItems";

interface PlaylistDisplayComponentProps {
  width?: number;
  height?: number;
  className?: string;
  prayerTimes?: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null;
  screenData?: ScreenData;
  masjidId?: number;
  prayerState?: PrayerState;
  // Previous Friday's Jummah times for detecting changes
  prevFridayJummah1?: string | null;
  prevFridayJummah2?: string | null;
  prevFridayJummah3?: string | null;
  prevFridayJummah4?: string | null;
  participantId?: string;
  // Per-component home page gap (independent of shared coordinator)
  componentGapEnabled?: boolean;
  componentGapDurationSeconds?: number;
}

type DisplayMode = "slideshow" | "changes" | "noticeboard" | "upcomingEvents";

const SimplePlaylistDisplayComponent: React.FC<
  PlaylistDisplayComponentProps
> = ({
  width = 300,
  height = 400,
  className = "",
  prayerTimes,
  tomorrowPrayerTimes,
  screenData,
  masjidId,
  prayerState,
  prevFridayJummah1 = null,
  prevFridayJummah2 = null,
  prevFridayJummah3 = null,
  prevFridayJummah4 = null,
  participantId,
  componentGapEnabled,
  componentGapDurationSeconds,
}) => {
  const [currentMode, setCurrentMode] = useState<DisplayMode | null>(null);
  const labbaikItems = useLabbaikItems(
    screenData?.masjidId,
    screenData?.showLabbaik,
  );
  const hasLabbaik = labbaikItems.length > 0;
  // With hideUpcomingEventsWhenNoEvents, events join the rotation only once we know
  // there are some, so the slot never shows a loading or empty events card.
  const [hasEvents, setHasEvents] = useState(false);
  const [next7DaysTimetable, setNext7DaysTimetable] = useState<PrayerTimes[]>(
    [],
  );
  const [currentRotation, setCurrentRotation] = useState(0);
  const [slideshowRestartKey, setSlideshowRestartKey] = useState(0);
  const [changesRestartKey, setChangesRestartKey] = useState(0);
  const [noticeboardRestartKey, setNoticeboardRestartKey] = useState(0);
  const [upcomingEventsRestartKey, setUpcomingEventsRestartKey] = useState(0);
  const [isWaitingForSharedGap, setIsWaitingForSharedGap] = useState(false);
  const [suppressChangesUntil, setSuppressChangesUntil] = useState(0);
  const [suppressUpcomingEventsUntil, setSuppressUpcomingEventsUntil] =
    useState(0);
  const [isLocalGapActive, setIsLocalGapActive] = useState(false);
  const localGapTimerRef = useRef<number | null>(null);
  const pendingResumeRef = useRef<{
    mode: DisplayMode;
    rotation: number;
  } | null>(null);
  const fallbackParticipantIdRef = useRef(
    `playlist-${Math.random().toString(36).slice(2)}`,
  );
  const resolvedParticipantId =
    participantId || fallbackParticipantIdRef.current;
  const {
    isGapActive: isSharedGapActive,
    gapCycle,
    registerParticipant,
    unregisterParticipant,
    setParticipantActive,
    reportCycleBoundary,
  } = useHomePageGapCoordinator();
  const handledGapCycleRef = useRef(gapCycle);

  // Throttle scheduler evaluations to a coarse time to avoid frequent recomputes
  const [currentTime, setCurrentTime] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const homePageGapEnabled = !!screenData?.showHomePageBetweenCycles;
  const homePageGapDurationSeconds = Math.min(
    Math.max(screenData?.homePageDisplayDurationSeconds ?? 20, 1),
    300,
  );
  // Per-component gap takes precedence over screen-level shared gap
  const useLocalGap = !!componentGapEnabled;
  const localGapDurationSeconds = Math.min(
    Math.max(componentGapDurationSeconds ?? 20, 1),
    300,
  );
  const refreshIntervalMs = Math.max(
    (screenData?.timesRefreshInterval ?? 15) * 60 * 1000,
    60 * 1000,
  );
  const isChangesSuppressed = suppressChangesUntil > currentTime.getTime();
  const isUpcomingEventsSuppressed =
    suppressUpcomingEventsUntil > currentTime.getTime();
  const hasSlideshowOnlySuppression = useMemo(() => {
    if (!screenData?.playlists || !prayerTimes) {
      return false;
    }

    const normalizedPlaylists = screenData.playlists.map((playlist) => {
      const normalized = { ...playlist };

      if (typeof normalized.scheduleType === "string") {
        normalized.scheduleType =
          ScheduleType[normalized.scheduleType as keyof typeof ScheduleType] ??
          ScheduleType.Always;
      }

      if (normalized.schedule && typeof normalized.schedule.type === "string") {
        (normalized.schedule as any).type = normalized.scheduleType;
      }

      return normalized;
    });

    const scheduler = new EnhancedPlaylistScheduler({ enableLogging: false });
    const evaluationResult = scheduler.evaluatePlaylists(
      normalizedPlaylists,
      prayerTimes,
      currentTime,
      screenData,
    );

    return evaluationResult.activePlaylists.some((ap) => {
      const playlist = ap.playlist;
      return (
        playlist?.isExclusiveWhileActive === true &&
        playlist?.isSlideshowSuppressingMode === true
      );
    });
  }, [screenData?.playlists, prayerTimes, currentTime, screenData]);

  const filteredEmbeddedScreenData: ScreenData | undefined = useMemo(() => {
    if (!screenData) {
      return screenData;
    }

    // Events and salah changes have their own turns in this rotation. The inner slideshow
    // adding them again showed them twice and ignored "hide when no events".
    const withoutOwnTurns = {
      ...screenData,
      showNextSalahChanges: false,
      showUpcomingEvents: false,
    };
    if (!hasSlideshowOnlySuppression) {
      return withoutOwnTurns;
    }

    return {
      ...withoutOwnTurns,
      displayMode: "slideshow",
      showHomePageBetweenCycles: false,
    };
  }, [screenData, hasSlideshowOnlySuppression]);

  useEffect(() => {
    registerParticipant(resolvedParticipantId);
    return () => {
      unregisterParticipant(resolvedParticipantId);
    };
  }, [registerParticipant, resolvedParticipantId, unregisterParticipant]);

  useEffect(() => {
    return () => {
      if (localGapTimerRef.current !== null) {
        window.clearTimeout(localGapTimerRef.current);
      }
    };
  }, []);

  // Calculate enabled modes based on screenData properties - memoized
  // Each feature works INDEPENDENTLY:
  // - showNextSalahChanges → changes mode (works in any displayMode)
  // - showUpcomingEvents (calendar widget) → upcomingEvents mode (works in any displayMode)
  // - regular playlists → slideshow or noticeboard based on displayMode
  const enabledModes = useMemo(() => {
    if (!screenData) {
      return [];
    }

    const isNoticeboardMode = screenData.displayMode === "noticeboard";

    // Check for noticeboard content (posters/images from playlists)
    const hasNoticeboardContent =
      isNoticeboardMode &&
      (hasLabbaik ||
        screenData.playlists?.some((playlist) =>
          playlist.items?.some(
            (item: any) =>
              item.assetType?.toLowerCase() === "poster" ||
              item.assetType?.toLowerCase() === "image" ||
              item.assetType?.toLowerCase() === "event" ||
              item.assetType?.toLowerCase() === "video",
          ),
        ));

    // Slideshow can run in any mode when suppression forces slideshow-only
    const hasValidSlideshowContent =
      hasLabbaik || screenData.playlists?.some((p) => p.items?.length > 0);

    // Order: noticeboard (posters), slideshow (non-noticeboard), upcomingEvents (calendar), changes (salah)
    const modes: DisplayMode[] = [];

    if (hasSlideshowOnlySuppression) {
      if (hasValidSlideshowContent) {
        modes.push("slideshow");
      }
      return modes;
    }

    // Add noticeboard mode if in noticeboard displayMode and has poster content
    if (hasNoticeboardContent) modes.push("noticeboard");

    // Add slideshow mode if NOT in noticeboard mode and has content
    if (!isNoticeboardMode && hasValidSlideshowContent) modes.push("slideshow");

    if (!hasSlideshowOnlySuppression) {
      // Add upcomingEvents mode (calendar widget) - works in ANY displayMode
      if (
        screenData.showUpcomingEvents &&
        !isUpcomingEventsSuppressed &&
        (!screenData.hideUpcomingEventsWhenNoEvents || hasEvents)
      ) {
        modes.push("upcomingEvents");
      }

      // Add changes mode (salah changes) - works in ANY displayMode
      if (screenData.showNextSalahChanges && !isChangesSuppressed) {
        modes.push("changes");
      }
    }

    // ...existing code...

    return modes;
  }, [
    screenData?.showNextSalahChanges,
    screenData?.showUpcomingEvents,
    screenData?.displayMode,
    screenData?.playlists,
    isChangesSuppressed,
    isUpcomingEventsSuppressed,
    hasSlideshowOnlySuppression,
    hasLabbaik,
    screenData?.hideUpcomingEventsWhenNoEvents,
    hasEvents,
  ]);

  // Only rotate between modes if we have multiple modes
  // Only reset mode/rotation if enabledModes array actually changes (not on every render or rotation)
  const prevEnabledModesRef = useRef<string[] | null>(null);
  useEffect(() => {
    const prev = prevEnabledModesRef.current;
    const enabledModesStr = enabledModes.join(",");
    const prevStr = prev ? prev.join(",") : null;

    if (!prev || enabledModesStr !== prevStr) {
      setIsWaitingForSharedGap(false);
      pendingResumeRef.current = null;

      // Only reset if enabledModes actually changed
      if (enabledModes.length === 0) {
        setCurrentMode(null);
        setCurrentRotation(0);
      } else if (enabledModes.length === 1) {
        setCurrentMode(enabledModes[0] as DisplayMode);
        setCurrentRotation(0);
      } else {
        setCurrentMode(enabledModes[0] as DisplayMode);
        setCurrentRotation(0);
      }
      prevEnabledModesRef.current = [...enabledModes];
    }
  }, [enabledModes]);

  const isSyncEligible =
    !useLocalGap && homePageGapEnabled && enabledModes.length > 0;

  useEffect(() => {
    setParticipantActive(resolvedParticipantId, isSyncEligible);

    if (!isSyncEligible) {
      setIsWaitingForSharedGap(false);
      pendingResumeRef.current = null;
    }
  }, [isSyncEligible, resolvedParticipantId, setParticipantActive]);

  useEffect(() => {
    if (gapCycle === handledGapCycleRef.current) {
      return;
    }

    handledGapCycleRef.current = gapCycle;

    if (!isWaitingForSharedGap) {
      return;
    }

    const pendingResume = pendingResumeRef.current;
    if (!pendingResume) {
      setIsWaitingForSharedGap(false);
      return;
    }

    setCurrentRotation(pendingResume.rotation);
    setCurrentMode(pendingResume.mode);
    setIsWaitingForSharedGap(false);
    pendingResumeRef.current = null;
  }, [gapCycle, isWaitingForSharedGap]);

  // Handler for when slideshow completes
  // Keep refs for enabledModes and currentRotation so handlers can stay stable
  const enabledModesRef = useRef<typeof enabledModes>(enabledModes);
  useEffect(() => {
    enabledModesRef.current = enabledModes;
  }, [enabledModes]);

  const currentRotationRef = useRef<number>(currentRotation);
  useEffect(() => {
    currentRotationRef.current = currentRotation;
  }, [currentRotation]);

  const moveToNextMode = useCallback(() => {
    if (isWaitingForSharedGap) {
      return;
    }

    const modes = enabledModesRef.current || [];
    const currRotation = currentRotationRef.current || 0;

    if (modes.length === 0) {
      setCurrentRotation(0);
      setCurrentMode(null);
      return;
    }

    const gapEnabled =
      !hasSlideshowOnlySuppression && (useLocalGap || homePageGapEnabled);
    const gapDuration = useLocalGap
      ? localGapDurationSeconds
      : homePageGapDurationSeconds;

    const startGap = (nextMode: DisplayMode, nextRotation: number) => {
      pendingResumeRef.current = { mode: nextMode, rotation: nextRotation };
      setIsWaitingForSharedGap(true);
      if (useLocalGap) {
        setIsLocalGapActive(true);
        if (localGapTimerRef.current !== null) {
          window.clearTimeout(localGapTimerRef.current);
        }
        localGapTimerRef.current = window.setTimeout(() => {
          localGapTimerRef.current = null;
          setIsLocalGapActive(false);
          const pending = pendingResumeRef.current;
          if (pending) {
            setCurrentRotation(pending.rotation);
            setCurrentMode(pending.mode);
            pendingResumeRef.current = null;
          }
          setIsWaitingForSharedGap(false);
        }, gapDuration * 1000);
      } else {
        reportCycleBoundary(resolvedParticipantId, gapDuration);
      }
    };

    if (modes.length === 1) {
      const onlyMode = modes[0] as DisplayMode;
      if (gapEnabled) {
        startGap(onlyMode, 0);
      } else {
        if (onlyMode === "slideshow") {
          setSlideshowRestartKey((prev) => prev + 1);
        } else if (onlyMode === "changes") {
          setChangesRestartKey((prev) => prev + 1);
        } else if (onlyMode === "noticeboard") {
          setNoticeboardRestartKey((prev) => prev + 1);
        } else if (onlyMode === "upcomingEvents") {
          setUpcomingEventsRestartKey((prev) => prev + 1);
        }
      }
      return;
    }

    const nextRotation = (currRotation + 1) % modes.length;
    const nextMode = modes[nextRotation] as DisplayMode;
    const completedFullRound = nextRotation === 0;

    if (gapEnabled && completedFullRound) {
      startGap(nextMode, nextRotation);
    } else {
      setCurrentRotation(nextRotation);
      setCurrentMode(nextMode);
    }
  }, [
    hasSlideshowOnlySuppression,
    useLocalGap,
    localGapDurationSeconds,
    homePageGapEnabled,
    homePageGapDurationSeconds,
    isWaitingForSharedGap,
    reportCycleBoundary,
    resolvedParticipantId,
  ]);

  const handleSlideshowComplete = useCallback(() => {
    moveToNextMode();
  }, [moveToNextMode]);

  const handleChangesComplete = useCallback(() => {
    moveToNextMode();
  }, [moveToNextMode]);

  const handleUpcomingEventsComplete = useCallback(() => {
    moveToNextMode();
  }, [moveToNextMode]);

  const handleNoticeboardComplete = useCallback(() => {
    moveToNextMode();
  }, [moveToNextMode]);

  const handleNoChanges = useCallback(() => {
    if (!screenData?.hideUpcomingSalahWhenNoChanges) return;
    setSuppressChangesUntil(Date.now() + refreshIntervalMs);
  }, [screenData?.hideUpcomingSalahWhenNoChanges, refreshIntervalMs]);

  const handleNoUpcomingEvents = useCallback(() => {
    if (!screenData?.hideUpcomingEventsWhenNoEvents) return;
    setSuppressUpcomingEventsUntil(Date.now() + refreshIntervalMs);
  }, [screenData?.hideUpcomingEventsWhenNoEvents, refreshIntervalMs]);

  useEffect(() => {
    if (
      !screenData?.showUpcomingEvents ||
      !screenData.hideUpcomingEventsWhenNoEvents ||
      !masjidId
    ) {
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const stored = await fetchEventsICS(
          masjidId,
          screenData.timesRefreshInterval || 300,
        );
        const events = [
          ...hydrateEvents(stored),
          ...labbaikEvents(labbaikItems, new Date()),
        ];
        if (!cancelled) setHasEvents(hasUpcomingEvents(events));
      } catch {
        if (!cancelled) setHasEvents(false);
      }
    };
    check();
    const timer = window.setInterval(check, refreshIntervalMs);
    window.addEventListener("events-updated", check);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("events-updated", check);
    };
  }, [
    masjidId,
    screenData?.showUpcomingEvents,
    screenData?.hideUpcomingEventsWhenNoEvents,
    screenData?.timesRefreshInterval,
    labbaikItems,
    refreshIntervalMs,
  ]);

  // Fetch next 7 days for changes display
  useEffect(() => {
    if (screenData?.showNextSalahChanges && masjidId && prayerTimes) {
      const fetchNext7Days = async () => {
        try {
          const lookaheadDays = getUpcomingSalahLookaheadDays(screenData);
          const timetableData = await fetchNext7DaysPrayerTimes(
            masjidId,
            lookaheadDays,
          );
          setNext7DaysTimetable(timetableData);
        } catch (error) {
          console.error("Error fetching next 7 days:", error);
        }
      };

      fetchNext7Days();
    }
  }, [
    screenData?.showNextSalahChanges,
    screenData?.upcomingSalahLookaheadDays,
    masjidId,
    prayerTimes,
    tomorrowPrayerTimes,
  ]);

  // Prepare playlists for slideshow - memoized to prevent unnecessary re-renders
  // ✅ NOW USES SCHEDULER TO EVALUATE ACTIVE PLAYLISTS
  const activePlaylists = useMemo(() => {
    if (!screenData?.playlists || !prayerTimes) {
      return [];
    }

    // Normalize playlists to fix type mismatches before passing to scheduler
    const normalizedPlaylists = screenData.playlists.map((playlist) => {
      // Create a shallow copy
      const normalized = { ...playlist };

      // Convert string scheduleType to enum value if needed
      if (typeof normalized.scheduleType === "string") {
        normalized.scheduleType =
          ScheduleType[normalized.scheduleType as keyof typeof ScheduleType] ??
          ScheduleType.Always;
      }

      // If schedule exists but its type is a string, update just the type property
      if (normalized.schedule && typeof normalized.schedule.type === "string") {
        // This avoids TypeScript errors by not creating a new schedule object
        // but directly modifying the type property of the existing one
        (normalized.schedule as any).type = normalized.scheduleType;
      }

      return normalized;
    });

    // Use scheduler to evaluate which playlists are currently active
    const scheduler = new EnhancedPlaylistScheduler({ enableLogging: false });
    const evaluationResult = scheduler.evaluatePlaylists(
      normalizedPlaylists,
      prayerTimes,
      currentTime,
      screenData, // Pass screenData for prayer-specific length configuration
    );

    // Get playlists that scheduler says are active
    const schedulerActivePlaylists = evaluationResult.activePlaylists.map(
      (ap) => ap.playlist,
    );

    const filteredPlaylists: Playlist[] = [];

    // All playlists are regular playlists now (virtual events playlist decommissioned)
    filteredPlaylists.push(...schedulerActivePlaylists);

    return filteredPlaylists;
  }, [screenData?.playlists, prayerTimes, currentTime]);

  // Prepare items passed to `SimpleNoticeboardDisplay` but keep a
  // stable reference unless the actual content changes.
  const rawNoticeboardItems = useMemo(() => {
    // Preparing noticeboard items from active playlists

    const now = new Date();
    const { regularPlaylists, injectPlaylists } = splitUrgentInjectPlaylists(
      activePlaylists,
      now,
    );

    const regularItems: any[] = [];
    const injectItems: any[] = [];

    const sortedRegular = sortByPriorityDesc(regularPlaylists);
    for (const playlist of sortedRegular) {
      let playlistItems = playlist.items || [];

      // Rotation mode: daily for date-based, weekly for time/prayer-based schedules
      // When enabled, shows only one item per rotation period using modulo
      if (playlist.isDailyRotation && playlistItems.length > 0) {
        playlistItems = getRotatedItems(
          playlist,
          playlistItems
            .slice()
            .sort(
              (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
            ),
          prayerTimes,
          "[SimplePlaylistDisplay]",
        );
      }

      regularItems.push(...playlistItems);
    }

    const sortedInject = sortByPriorityDesc(injectPlaylists);
    for (const playlist of sortedInject) {
      const sortedItems = (playlist.items || [])
        .slice()
        .sort(
          (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
        );

      injectItems.push(
        ...sortedItems.map((item: any) => ({
          ...item,
          duration: Math.max(
            item.duration || 10,
            playlist.urgentMinShowSeconds || 10,
          ),
          isUrgentInjectItem: true,
        })),
      );
    }

    const injectCadence = getUrgentInjectCadence(sortedInject);

    const items = mergeInjectItems(regularItems, injectItems, injectCadence)
      .filter((item: any) => {
        const t = (item.assetType || "").toString().toLowerCase();
        return (
          t === "poster" || t === "image" || t === "event" || t === "video"
        );
      })
      // Filter by poster category (showEventPosters, showFuneralNotices, showInformationPosters)
      .filter((item: any) => {
        const category = item.eventCategory || "Event";
        const shouldShow = screenData
          ? shouldShowCategory(screenData, category)
          : true;
        if (!shouldShow) {
          console.log(
            `[SimplePlaylistDisplay] Filtering out item "${item.assetTitle || item.title}" with category "${category}"`,
          );
        }
        return shouldShow;
      })
      .map((item: any) => ({
        id: Number(item.id ?? item.assetId ?? 0),
        assetId: Number(item.assetId ?? item.id ?? 0),
        assetTitle: item.assetTitle ?? item.title ?? "",
        assetType: item.assetType ?? "",
        assetUrl: item.assetUrl ?? item.url ?? "",
        duration: item.duration ?? undefined,
        videoType: item.videoType ?? null,
        videoDurationSeconds: item.videoDurationSeconds ?? null,
        isVideoUrl: item.isVideoUrl ?? undefined,
        videoAutoplay: item.videoAutoplay ?? undefined,
        videoMuted: item.videoMuted ?? undefined,
        videoLoop: item.videoLoop ?? undefined,
        displayOrder: item.displayOrder ?? undefined,
        isActive: item.isActive ?? true,
        eventCategory: item.eventCategory ?? "Event", // Pass through poster category
      }));

    // Add upcoming events widget as a special item if enabled and in noticeboard mode
    if (
      screenData?.showUpcomingEvents &&
      screenData?.displayMode === "noticeboard"
    ) {
      items.push({
        id: -999, // Special ID for upcoming events widget
        assetId: -999,
        assetTitle: "Upcoming Events",
        assetType: "upcomingEventsWidget",
        assetUrl: "",
        duration: screenData.upcomingEventsDuration || 15,
        videoType: null,
        videoDurationSeconds: null,
        isVideoUrl: undefined,
        videoAutoplay: undefined,
        videoMuted: undefined,
        videoLoop: undefined,
        displayOrder: undefined,
        isActive: true,
        eventCategory: "Event",
      });
    }

    return items;
  }, [
    activePlaylists,
    screenData?.showUpcomingEvents,
    screenData?.displayMode,
    screenData?.upcomingEventsDuration,
    screenData?.showEventPosters,
    screenData?.showFuneralNotices,
    screenData?.showInformationPosters,
    screenData,
    prayerTimes?.HijriDateComponents, // ✅ Re-evaluate when Hijri date changes (for daily rotation)
  ]);

  const noticeboardItemsRef = useRef<any[]>([]);
  const noticeboardItemsJsonRef = useRef<string>("");
  const noticeboardUpdateCountRef = useRef<number>(0);

  const simpleNoticeboardItems = useMemo(() => {
    const json = JSON.stringify(rawNoticeboardItems);
    if (json !== noticeboardItemsJsonRef.current) {
      noticeboardItemsJsonRef.current = json;
      noticeboardItemsRef.current = rawNoticeboardItems;
      noticeboardUpdateCountRef.current += 1;
      // noticeboardItems updated
    }
    return noticeboardItemsRef.current;
  }, [rawNoticeboardItems]);

  if (
    !enabledModes.length ||
    (useLocalGap ? isLocalGapActive : isSharedGapActive)
  ) {
    return null;
  }

  return (
    <div
      className={`playlist-display-component ${className}`}
      style={{ width, height }}
    >
      {currentMode === "slideshow" &&
        (activePlaylists.length > 0 || hasLabbaik) &&
        screenData &&
        prayerTimes &&
        prayerState && (
          <div
            style={{ width, height }}
            className={`embedded-slideshow ${className}`}
          >
            <SimpleEmbeddedSlideshowComponent
              key={`slideshow-${screenData?.masjidId || "default"}-${slideshowRestartKey}`}
              width={width}
              height={height}
              playlists={activePlaylists}
              screenData={filteredEmbeddedScreenData || screenData}
              prayerTimes={prayerTimes}
              prayerState={prayerState}
              onComplete={handleSlideshowComplete}
              forceContinuousLoop={hasSlideshowOnlySuppression}
              prevFridayJummah1={prevFridayJummah1}
              prevFridayJummah2={prevFridayJummah2}
              prevFridayJummah3={prevFridayJummah3}
              prevFridayJummah4={prevFridayJummah4}
              tomorrowPrayerTimes={tomorrowPrayerTimes}
              restartTrigger={slideshowRestartKey}
            />
          </div>
        )}

      {currentMode === "changes" &&
        next7DaysTimetable.length > 0 &&
        prayerTimes &&
        screenData && (
          <SimpleUpcomingSalahChangesSlideshow
            restartTrigger={changesRestartKey}
            timetables={next7DaysTimetable}
            customizedNames={screenData.customizedNames ?? {}}
            duration={screenData.nextSalahChangesDuration || 10}
            onComplete={handleChangesComplete}
            prayerTimes={prayerTimes}
            tomorrowPrayerTimes={tomorrowPrayerTimes || null}
            screenData={screenData}
            prevFridayJummah1={prevFridayJummah1}
            prevFridayJummah2={prevFridayJummah2}
            prevFridayJummah3={prevFridayJummah3}
            prevFridayJummah4={prevFridayJummah4}
            width={width}
            height={height}
            primaryColor={screenData.primaryColor}
            secondaryColor={screenData.secondaryColor}
            accentColor={screenData.accentColor}
            hideWhenNoChanges={screenData.hideUpcomingSalahWhenNoChanges}
            onNoChanges={handleNoChanges}
          />
        )}

      {currentMode === "noticeboard" &&
        (simpleNoticeboardItems.length > 0 || hasLabbaik) &&
        screenData &&
        prayerTimes &&
        prayerState && (
          <div
            style={{ width, height }}
            className={`embedded-noticeboard ${className}`}
          >
            {/* Add a key that ONLY changes when we actually want to restart */}
            <SimpleNoticeboardDisplay
              key={`noticeboard-${noticeboardRestartKey}`}
              items={simpleNoticeboardItems}
              screenData={screenData}
              width={width}
              height={height}
              onComplete={handleNoticeboardComplete}
              masjidId={masjidId}
            />
          </div>
        )}

      {currentMode === "upcomingEvents" && screenData && masjidId && (
        <div
          style={{ width, height }}
          className={`embedded-upcoming-events ${className}`}
        >
          {/* No key prop - use restartTrigger instead to avoid remounting and re-fetching */}
          <SimpleUpcomingEventsWidget
            masjidId={masjidId}
            duration={screenData.upcomingEventsDuration || 15}
            onComplete={handleUpcomingEventsComplete}
            screenData={screenData}
            primaryColor={screenData.primaryColor}
            secondaryColor={screenData.secondaryColor}
            accentColor={screenData.accentColor}
            width={width}
            height={height}
            restartTrigger={upcomingEventsRestartKey}
            hideWhenNoEvents={screenData.hideUpcomingEventsWhenNoEvents}
            onNoEvents={handleNoUpcomingEvents}
          />
        </div>
      )}

      {enabledModes.length > 1 && (
        <div className="rotation-indicator">
          {enabledModes.map((mode, index) => (
            <div
              key={mode}
              className={`indicator-dot ${
                index === currentRotation ? "active" : ""
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SimplePlaylistDisplayComponent;
